// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { QuotaProvider } from "./context/QuotaContext";
import { CartProvider } from "./context/CartContext";

import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase/firebase";
import Maintenance from "./pages/Maintenance";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import CompleteSignup from "./components/CompleteSignup";
import AuthAction from "./components/AuthAction";
import CheckYourEmail from "./pages/CheckYourEmail";
import Dashboard from "./pages/Dashboard";
import ChatInterface from "./components/ChatInterface";
import VoiceInterface from "./components/VoiceInterface";
import BillingHistory from "./pages/Billing";
import Checkout from "./pages/Checkout";
import Cart from "./pages/Cart";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Layout from "./components/Layout";
import VerifyOTP from "./pages/VerifyOTP";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ForgotPassword from "./pages/ForgotPassword";
import Profile from "./pages/Profile";
import SmartAlertPage from "./pages/SmartAlertPage";
import PaymentComplete from "./pages/PaymentComplete";
import DocWriterStartPage from "./pages/DocWriterStartPage";
import DocWriterEditorPage from "./pages/DocWriterEditorPage";
import DocHistoryPage from "./pages/DocHistoryPage";
import DocViewerPage from "./pages/DocViewerPage";
import CaseAnalyzerPage from "./pages/CaseAnalyzerPage";
import CaseAnalyzerStatus from "./pages/CaseAnalyzerStatus";
import CaseAnalyzerHistory from "./pages/CaseAnalyzerHistory";
import CaseAnalyzerDashboard from "./pages/CaseAnalyzerDashboard";
import CostEstimatorPage from "./pages/CostEstimatorPage";
import CaseValueEstimatorPage from "./pages/CaseValueEstimatorPage";
import FileVaultPage from "./pages/FileVaultPage";
import FileViewerPage from "./pages/FileViewerPage";
import OptIn from "./pages/OptIn";
import NotFound from "./pages/NotFound";
import ContractInsightPage from "./pages/ContractInsightPage";
import ContractInsightStatus from "./pages/ContractInsightStatus";
import ContractInsightHistory from "./pages/ContractInsightHistory";
import ContractInsightDashboard from "./pages/ContractInsightDashboard";
import VisionAssistLauncher from "./pages/VisionAssistLauncher";
import VisionAssistSession from "./pages/VisionAssistSession";
import ConnectReturn from "./pages/ConnectReturn";
import ConnectRefresh from "./pages/ConnectRefresh";

/*
 * MAINTENANCE CONTROL
 *
 * Change ONLY this line when you want to control maintenance from the frontend:
 *
 * "on"        = always show the maintenance page
 * "off"       = always show the normal application
 * "firestore" = use /settings/public_config -> maintenanceMode
 *
 * It is temporarily set to "on" so the maintenance page must appear after deploy.
 */
const MAINTENANCE_CONTROL = "firestore";

function App() {
  const [firestoreMaintenanceMode, setFirestoreMaintenanceMode] = useState(false);
  const [loadingMaintenance, setLoadingMaintenance] = useState(true);

  useEffect(() => {
    const maintenanceDocRef = doc(db, "settings", "public_config");

    const unsubscribe = onSnapshot(
      maintenanceDocRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        const maintenanceValue = data?.maintenanceMode;
        const isMaintenanceEnabled = maintenanceValue === true;

        console.info("[Maintenance] Firestore document read completed.", {
          path: "settings/public_config",
          exists: snapshot.exists(),
          maintenanceMode: maintenanceValue,
          maintenanceModeType: typeof maintenanceValue,
          enabled: isMaintenanceEnabled,
          fromCache: snapshot.metadata.fromCache,
        });

        if (
          snapshot.exists() &&
          typeof maintenanceValue !== "boolean"
        ) {
          console.warn(
            "[Maintenance] The maintenanceMode field exists but is not a Boolean.",
            maintenanceValue
          );
        }

        setFirestoreMaintenanceMode(isMaintenanceEnabled);
        setLoadingMaintenance(false);
      },
      (error) => {
        console.error(
          "[Maintenance] Failed to read settings/public_config.",
          {
            code: error?.code,
            message: error?.message,
            error,
          }
        );

        // Fail open: keep the normal application available if Firestore cannot be read.
        setFirestoreMaintenanceMode(false);
        setLoadingMaintenance(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const maintenanceMode =
    MAINTENANCE_CONTROL === "on"
      ? true
      : MAINTENANCE_CONTROL === "off"
        ? false
        : firestoreMaintenanceMode;

  // Only wait for Firestore when Firestore is controlling maintenance mode.
  if (MAINTENANCE_CONTROL === "firestore" && loadingMaintenance) {
    return null;
  }

  if (maintenanceMode) {
    return <Maintenance />;
  }

  return (
    <AuthProvider>
      <QuotaProvider>
        <CartProvider>
          <Router>
            <Routes>
              <Route path="/" element={<Layout><Home /></Layout>} />

              {/* Optional: /home alias so Sidebar "Home" can link here safely */}
              <Route path="/home" element={<Layout><Home /></Layout>} />

              {/* Auth */}
              <Route path="/login" element={<Layout><Login /></Layout>} />
              <Route path="/signup" element={<Layout><Signup /></Layout>} />
              <Route path="/check-your-email" element={<Layout><CheckYourEmail /></Layout>} />
              <Route path="/complete-signup" element={<Layout><CompleteSignup /></Layout>} />
              <Route path="/auth/action" element={<Layout><AuthAction /></Layout>} />
              <Route path="/verify-otp" element={<Layout><VerifyOTP /></Layout>} />
              <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />

              {/* Main app */}
              <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
              <Route path="/chat" element={<Layout><ChatInterface /></Layout>} />
              <Route path="/voice" element={<Layout><VoiceInterface /></Layout>} />

              {/* Billing flow */}
              <Route path="/billing" element={<Layout><BillingHistory /></Layout>} />
              <Route path="/cart" element={<Layout><Cart /></Layout>} />
              <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
              <Route path="/payment-complete" element={<Layout><PaymentComplete /></Layout>} />

              {/* Static pages */}
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/terms" element={<Layout><Terms /></Layout>} />
              <Route path="/privacy" element={<Layout><Privacy /></Layout>} />

              {/* Profile */}
              <Route path="/profile" element={<Layout><Profile /></Layout>} />

              {/* Smart Alert */}
              <Route path="/smart-alert" element={<Layout><SmartAlertPage /></Layout>} />
              <Route path="/optin" element={<Layout><OptIn /></Layout>} />

              {/* DocWriter */}
              <Route path="/docwriter/start" element={<Layout><DocWriterStartPage /></Layout>} />
              <Route path="/docwriter/editor" element={<Layout><DocWriterEditorPage /></Layout>} />
              <Route path="/docwriter/history" element={<Layout><DocHistoryPage /></Layout>} />
              <Route path="/docwriter/viewer" element={<Layout><DocViewerPage /></Layout>} />

              {/* Case Analyzer */}
              <Route path="/case-analyzer" element={<Layout><CaseAnalyzerPage /></Layout>} />
              <Route path="/case-analyzer/status" element={<Layout><CaseAnalyzerStatus /></Layout>} />
              <Route path="/case-analyzer/history" element={<Layout><CaseAnalyzerHistory /></Layout>} />
              <Route path="/case-analyzer/dashboard" element={<Layout><CaseAnalyzerDashboard /></Layout>} />
              <Route path="/cost-pilot" element={<Layout><CostEstimatorPage /></Layout>} />
              <Route path="/case-value" element={<Layout><CaseValueEstimatorPage /></Layout>} />

              {/* Contract Insight */}
              <Route path="/contract-insight" element={<Layout><ContractInsightPage /></Layout>} />
              <Route path="/contract-insight/status" element={<Layout><ContractInsightStatus /></Layout>} />
              <Route path="/contract/status" element={<Layout><ContractInsightStatus /></Layout>} />
              <Route path="/contract-insight/history" element={<Layout><ContractInsightHistory /></Layout>} />
              <Route path="/contract-insight/dashboard" element={<Layout><ContractInsightDashboard /></Layout>} />

              {/* FileVault */}
              <Route path="/file-vault" element={<Layout><FileVaultPage /></Layout>} />
              <Route path="/file-viewer" element={<Layout><FileViewerPage /></Layout>} />

              {/* Vision Assist */}
              <Route path="/vision-assist" element={<Layout><VisionAssistLauncher /></Layout>} />
              <Route path="/vision/session/:sessionId" element={<Layout><VisionAssistSession /></Layout>} />
             
              {/* Stripe Connect */}
              <Route path="/connect/return" element={<ConnectReturn />} />
              <Route path="/connect/refresh" element={<ConnectRefresh />} />

              {/* Not found */}
              <Route path="*" element={<Layout><NotFound /></Layout>} />
            </Routes>
          </Router>
        </CartProvider>
      </QuotaProvider>
    </AuthProvider>
  );
}

export default App;
